import { notification } from "antd"
import type { HookAPI } from "antd/es/modal/useModal"
import type { NavigateFunction } from "react-router-dom"

import { notificationOptions } from "../../../constants/notification.config.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { budgetFormat } from "../../../utils/number/budget-format.ts"

export interface InvoiceApiErrorDetails {
  message?: string
  detail?: string
  missing_branding?: string[]
  missing_lines?: string[] | string
  missing_project?: string[] | string
  missing_customer?: string[] | string
  over_billed?: Array<{
    finance_line: string
    name?: string
    balance_remaining?: string
  }>
  field_errors?: Record<string, string[]>
  [key: string]: unknown
}

type Translate = (key: string, options?: Record<string, unknown>) => string

export const extractInvoiceErrorDetails = (
  payload: unknown
): InvoiceApiErrorDetails =>
  ((payload as { details?: InvoiceApiErrorDetails } | undefined)?.details ??
    {}) as InvoiceApiErrorDetails

// Reported against ledger lines rather than a form field, so it has its own
// rendering path via onOverBilled.
const NON_FORM_FIELD_ERRORS = new Set(["over_billed"])

// Keys the envelope carries that are never form fields.
const RESERVED_KEYS = new Set([
  "type",
  "message",
  "description",
  "data",
  "status_code",
  "detail",
  "code"
])

// Keys with dedicated rendering paths in handleInvoiceApiError.
const HANDLED_KEYS = new Set([
  "field_errors",
  "missing_branding",
  "missing_lines",
  "missing_project",
  "missing_customer",
  "over_billed"
])

const toStringArray = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [value]).map(String)

const flatFieldErrors = (
  details: InvoiceApiErrorDetails
): Array<{ name: string; errors: string[] }> =>
  Object.entries(details)
    .filter(([name, value]) => {
      if (RESERVED_KEYS.has(name) || HANDLED_KEYS.has(name)) return false
      return (
        typeof value === "string" ||
        (Array.isArray(value) && value.every((item) => typeof item === "string"))
      )
    })
    .map(([name, value]) => ({ name, errors: toStringArray(value) }))

export const invoiceFormFieldErrors = (
  payload: unknown
): Array<{ name: string; errors: string[] }> => {
  const details = extractInvoiceErrorDetails(payload)
  const nested = Object.entries(details.field_errors ?? {})
    .filter(([name]) => !NON_FORM_FIELD_ERRORS.has(name))
    .map(([name, errors]) => ({ name, errors: toStringArray(errors) }))

  const seen = new Set(nested.map((entry) => entry.name))
  const flat = flatFieldErrors(details).filter(
    (entry) => !seen.has(entry.name)
  )

  return [...nested, ...flat]
}

interface HandleInvoiceApiErrorArgs {
  payload: unknown
  modal: HookAPI
  t: Translate
  navigate: NavigateFunction
  onOverBilled?: (errors: Record<string, string>) => void
}

export const handleInvoiceApiError = ({
  payload,
  modal,
  t,
  navigate,
  onOverBilled
}: HandleInvoiceApiErrorArgs) => {
  const details = extractInvoiceErrorDetails(payload)

  if (details.missing_branding) {
    modal.confirm({
      title: t("invoices.drawer.missingBranding.title"),
      content: t("invoices.drawer.missingBranding.body"),
      okText: t("invoices.drawer.missingBranding.cta"),
      cancelText: t("invoices.drawer.cancel"),
      onOk: () => navigate(RoutesPaths.settings.company)
    })
    return
  }

  if (details.missing_customer) {
    notification.error({
      ...notificationOptions.error,
      message: t("invoices.drawer.missingCustomer")
    })
    return
  }

  if (details.missing_lines) {
    notification.error({
      ...notificationOptions.error,
      message: t("invoices.drawer.missingLines")
    })
    return
  }

  const overBilled = details.over_billed ?? details.field_errors?.over_billed
  if (Array.isArray(overBilled) && onOverBilled) {
    const next: Record<string, string> = {}
    overBilled.forEach((item) => {
      if (typeof item === "object" && item?.finance_line) {
        next[item.finance_line] = t("invoices.drawer.overBilled", {
          balance: budgetFormat(item.balance_remaining ?? "0")
        })
      }
    })
    onOverBilled(next)
  }

  if (details.message === "Validation error") {
    const flat = flatFieldErrors(details)
    if (flat.length) {
      flat.forEach((entry) => {
        notification.error({
          ...notificationOptions.error,
          message: entry.errors.join(" ")
        })
      })
      return
    }
  }

  const detailMessage =
    typeof details.detail === "string" ? details.detail : undefined
  notification.error({
    ...notificationOptions.error,
    message: details.message || detailMessage || t("invoices.drawer.saveFailed")
  })
}
